import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { DashboardProvider, useDashboard } from '@/contexts/DashboardContext';
import { AudioProvider, useAudio } from '@/contexts/AudioContext';
import MapView from '@/components/MapView';
import ScannerFeedsSidebar from '@/components/ScannerFeedsSidebar';
import FilterPanel from '@/components/FilterPanel';
import ScannerFeed from '@/components/ScannerFeed';
import AIChat, { AIFloatingButton } from '@/components/AIChat';
import ThemeToggle from '@/components/ThemeToggle';
import { TrialCountdownBanner, TrialWarningModal } from '@/components/TrialCountdown';
import { IncidentAlertContainer } from '@/components/IncidentAlerts';
import useWebSocket from '@/hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { 
  Radio, 
  Filter, 
  User, 
  LogOut, 
  Crown, 
  RefreshCw,
  ChevronDown,
  Volume2,
  VolumeX,
  Headphones,
  Target,
  Bell,
  Bot,
  Package,
  Layers,
  Eye,
  Pause,
  Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Import safety components for quick access dialogs
import { 
  IncidentTimelineSheet,
  PropertySafetyDialog,
  RouteSafetyDialog,
  CommunityReportsDialog
} from '@/components/safety';

// Import new P1/P2 feature components
import ScannerTranscriptionPanel, { TranscriptionFloatingButton } from '@/components/ScannerTranscriptionPanel';
import TranscriptionHistoryPanel, { TranscriptionHistoryButton } from '@/components/TranscriptionHistoryPanel';
import { ImSafeButton } from '@/components/ImSafeButton';
import { ShareButton, SHARE_TYPES } from '@/components/ShareButton';
import { GamificationPanel, GamificationButton } from '@/components/GamificationPanel';
import WatchZoneAlertsPanel, { WatchZoneAlertButton } from '@/components/WatchZoneAlertsPanel';

// Import dashboard components
import { 
  ScannerBar, 
  LocationSelector, 
  DataSourceToggle,
  TankBanner,
  QuickAccessTiles,
  BottomTicker,
  DataOverlaysDropdown,
  LayerDetailsDialog
} from '@/components/dashboard';
import NotificationSettings from '@/components/NotificationSettings';
import WatchZoneManager from '@/components/WatchZoneManager';

const DashboardContent = () => {
  const { user, token, logout } = useAuth();
  const { isPaid, getTierInfo, trialActive } = useSubscription();
  const { currentScanner, isPlaying, playScanner, stopScanner, isLoading } = useAudio();
  const { 
    cameras, 
    offenders, 
    weatherAlerts,
    scannerFeeds,
    stats,
    drillPath,
    allMapItems,
    handleDrillDown,
    selectedIncident,
    setSelectedIncident,
    activeFilters,
    setActiveFilters,
    handleSelectScannerFeed,
    states,
    counties,
    precincts,
    selectedScannerFeed,
    setSelectedScannerFeed,
    activeDataSources,
    toggleDataSource,
    filterBySeverity,
    filterByType,
    refreshing,
    handleRefresh,
    scannerPlaying,
    setScannerPlaying,
    scannerOpen,
    setScannerOpen,
    scannerMinimized,
    setScannerMinimized,
    chatOpen,
    setChatOpen,
    loading,
    userTier,
    canAccessOffenders,
    canAccessCameras,
    aiChatOpen,
    setAiChatOpen
  } = useDashboard();
  const navigate = useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [notifSettingsOpen, setNotifSettingsOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  // New P1/P2 feature states
  const [transcriptionOpen, setTranscriptionOpen] = useState(false);
  const [transcriptionHistoryOpen, setTranscriptionHistoryOpen] = useState(false);
  const [gamificationOpen, setGamificationOpen] = useState(false);
  const [watchZoneAlertsOpen, setWatchZoneAlertsOpen] = useState(false);
  
  // Map layer visibility state - Load from localStorage or use defaults
  const [mapLayers, setMapLayers] = useState(() => {
    const saved = localStorage.getItem('veriwatch_map_layers');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved layer preferences');
      }
    }
    return {
      scanners: true,  // Scanners always ON by default - core feature
      sexOffenders: false,
      cameras: false,
      weather: false,
      airQuality: false,
      diseaseOutbreaks: false,
      foodSafety: false,
      terrorism: false
    };
  });
  
  // Save layer preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('veriwatch_map_layers', JSON.stringify(mapLayers));
  }, [mapLayers]);
  
  // State for map focus when playing a scanner
  const [mapFocusLocation, setMapFocusLocation] = useState(null);
  
  // Watch Zones state - saved to localStorage
  const [watchZones, setWatchZones] = useState(() => {
    const saved = localStorage.getItem('veriwatch_watch_zones');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved watch zones');
      }
    }
    return [];
  });
  
  // Save watch zones to localStorage
  useEffect(() => {
    localStorage.setItem('veriwatch_watch_zones', JSON.stringify(watchZones));
  }, [watchZones]);
  
  // State for layer details modal
  const [layerDetailsOpen, setLayerDetailsOpen] = useState(false);
  const [selectedLayerDetails, setSelectedLayerDetails] = useState(null);
  
  // Handler for showing layer details
  const handleShowLayerDetails = useCallback((layer) => {
    setSelectedLayerDetails(layer);
    setLayerDetailsOpen(true);
  }, []);
  
  // Handler for when a scanner starts playing - also plays audio
  const handleScannerPlay = useCallback((scanner) => {
    // Set map focus location
    if (scanner.lat && scanner.lng) {
      setMapFocusLocation({
        lat: scanner.lat,
        lng: scanner.lng,
        zoom: 11,
        name: scanner.name
      });
    } else if (scanner.location) {
      setMapFocusLocation({
        lat: scanner.location.lat,
        lng: scanner.location.lng,
        zoom: 11,
        name: scanner.name
      });
    }
    
    // Play the scanner audio
    playScanner(scanner);
  }, [playScanner]);
  
  // Handle scanner selection from map
  const handleMapScannerSelect = useCallback((scanner) => {
    handleScannerPlay(scanner);
  }, [handleScannerPlay]);
  
  // Get user's location for alerts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          // Default to San Diego if geolocation fails
          setUserLocation({ lat: 32.7157, lng: -117.1611 });
        }
      );
    }
  }, []);
  
  // WebSocket for real-time updates
  const { lastMessage } = useWebSocket();

  // Handle real-time WebSocket alerts
  useEffect(() => {
    if (lastMessage?.type === 'osint_alert' || lastMessage?.type === 'critical_alert') {
      handleRefresh();
    }
  }, [lastMessage, handleRefresh]);

  const scrollToFeed = () => {
    const feedElement = document.querySelector('[data-testid="incident-sidebar"]');
    if (feedElement) {
      feedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleStatClick = (filterType, filterValue) => {
    scrollToFeed();
    if (filterType === 'severity') {
      filterBySeverity(filterValue);
    } else if (filterType === 'type') {
      filterByType(filterValue);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get subscription tier info
  const subTierInfo = getTierInfo();
  const tierInfo = {
    label: subTierInfo.displayName,
    color: subTierInfo.color === 'teal' ? 'bg-teal-600' : subTierInfo.color === 'amber' ? 'bg-amber-600' : subTierInfo.color === 'orange' ? 'bg-orange-600' : 'bg-slate-600'
  };

  // Count active layers
  const activeLayerCount = Object.values(mapLayers).filter(Boolean).length;

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-950" data-testid="dashboard">
      {/* Real-time Incident Alerts */}
      <IncidentAlertContainer userLocation={userLocation} enabled={isPaid()} />
      
      {/* Trial Countdown Banner */}
      {trialActive && <TrialCountdownBanner />}
      
      {/* Map Layer */}
      <MapView 
        incidents={mapLayers.incidents ? allMapItems : []}
        selectedIncident={selectedIncident}
        onSelectIncident={setSelectedIncident}
        filters={activeFilters}
        drillPath={drillPath}
        onDrillDown={handleDrillDown}
        stats={stats}
        scannerFeeds={mapLayers.scanners ? scannerFeeds : []}
        onSelectScannerFeed={handleMapScannerSelect}
        focusLocation={mapFocusLocation}
        cameras={mapLayers.cameras ? cameras : []}
        offenders={mapLayers.sexOffenders ? offenders : []}
        weatherAlerts={mapLayers.weather ? weatherAlerts : []}
      />
      
      {/* Now Playing Banner - Shows when scanner is active */}
      {currentScanner && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] glass-card border border-teal-500/50 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg shadow-teal-500/20">
          <div className="flex items-center gap-2">
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <div 
                    key={i}
                    className="w-1 bg-teal-400 rounded-full animate-pulse"
                    style={{ 
                      height: `${8 + Math.random() * 10}px`,
                      animationDelay: `${i * 0.1}s`
                    }}
                  />
                ))}
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-white">{currentScanner.name}</p>
              <p className="text-[10px] text-teal-400">{currentScanner.city}, {currentScanner.state}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={stopScanner}
            className="h-8 w-8 p-0 rounded-full bg-teal-500/20 hover:bg-teal-500/30 text-teal-400"
          >
            <Pause className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {/* Empty State Overlay - When no layers selected AND no scanner playing */}
      {activeLayerCount === 0 && !mapFocusLocation && !currentScanner && (
        <div className="absolute inset-0 z-[15] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
          <div className="text-center max-w-md mx-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-600/20 to-slate-800 border border-teal-500/30 flex items-center justify-center">
              <Layers className="w-10 h-10 text-teal-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Select Your Data Overlays</h3>
            <p className="text-slate-400 text-sm mb-6">
              Use the Data Overlays button in the header to choose what data to display.
            </p>
            <p className="text-xs text-slate-500">
              Click a scanner feed on the left to start listening
            </p>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <header className="absolute top-0 left-0 right-0 z-20 glass-card border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo & Brand */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_presence-check-26/artifacts/zsrnp3ib_veriwatch_dark.png"
                alt="VeriWatch"
                className="h-8 w-auto"
              />
            </Link>
            
            {/* Data Overlays Dropdown */}
            <DataOverlaysDropdown 
              mapLayers={mapLayers}
              setMapLayers={setMapLayers}
              isPaid={isPaid}
              onShowLayerDetails={handleShowLayerDetails}
            />
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            <ThemeToggle />

            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setScannerPlaying(!scannerPlaying)}
              className={`text-slate-400 hover:text-slate-200 hover:bg-slate-800 ${scannerPlaying ? 'text-teal-400' : ''}`}
              data-testid="scanner-toggle"
            >
              {scannerPlaying ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>

            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleRefresh}
              className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setScannerOpen(true);
                setScannerMinimized(false);
              }}
              className="text-slate-400 hover:text-slate-200 hover:bg-slate-800 relative"
              data-testid="scanner-btn"
            >
              <Headphones className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </Button>
            
            {/* Watch Zones Manager */}
            <WatchZoneManager 
              watchZones={watchZones}
              setWatchZones={setWatchZones}
              userLocation={userLocation}
            />

            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                  data-testid="filter-btn"
                >
                  <Filter className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] glass-card border-slate-800 p-0 overflow-y-auto">
                <DataSourceToggle 
                  activeDataSources={activeDataSources}
                  toggleDataSource={toggleDataSource}
                  userTier={userTier}
                  canAccessOffenders={canAccessOffenders}
                  canAccessCameras={canAccessCameras}
                />
                
                <FilterPanel 
                  activeFilters={activeFilters}
                  onFilterChange={setActiveFilters}
                  userTier={user?.tier || 'free'}
                />
                <div className="border-t border-slate-800">
                  <LocationSelector
                    drillPath={drillPath}
                    onSelect={handleDrillDown}
                    states={states}
                    counties={counties}
                    precincts={precincts}
                  />
                </div>
              </SheetContent>
            </Sheet>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/ops')}
              className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              data-testid="ops-btn"
              title="Tactical Ops Center"
            >
              <Target className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/safety')}
              className="text-slate-400 hover:text-teal-400 hover:bg-slate-800"
              data-testid="safety-intel-btn"
              title="Safety Intelligence"
            >
              <Shield className="w-4 h-4" />
            </Button>

            {/* Gamification / Achievements */}
            <GamificationButton onClick={() => setGamificationOpen(true)} />

            {/* Watch Zone Alerts */}
            <WatchZoneAlertButton onClick={() => setWatchZoneAlertsOpen(true)} />

            {/* Share Button */}
            <ShareButton type={SHARE_TYPES.GENERAL} variant="ghost" size="icon" />

            {isPaid() && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/command-center')}
                className="text-teal-400 hover:text-teal-300 hover:bg-slate-800 relative"
                data-testid="command-center-btn"
                title="Tank's Command Center"
              >
                <Bot className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-400 rounded-full" />
              </Button>
            )}

            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-950/50 border border-blue-800/30">
              <Eye className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs text-blue-300 font-medium">OverWatch</span>
            </div>

            {/* User menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-slate-300 hover:text-slate-100 hover:bg-slate-800"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                      <span className="text-xs font-bold text-white">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:inline text-sm">{user.name?.split(' ')[0]}</span>
                    <Badge className={`${tierInfo.color} text-white text-[10px] px-1.5`}>
                      {tierInfo.label}
                    </Badge>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 glass-card border-slate-800">
                  <DropdownMenuLabel className="text-slate-400 font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-200">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => navigate('/frequencies')}
                  >
                    <Radio className="mr-2 h-4 w-4 text-teal-400" />
                    <span>Frequencies</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => navigate('/pricing')}
                  >
                    <Crown className="mr-2 h-4 w-4 text-amber-500" />
                    <span>Pricing</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => navigate('/supplies')}
                  >
                    <Package className="mr-2 h-4 w-4 text-orange-400" />
                    <span>Emergency Supplies</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => navigate('/subscription')}
                  >
                    <Crown className="mr-2 h-4 w-4 text-amber-500" />
                    <span>Manage Subscription</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => setNotifSettingsOpen(true)}
                  >
                    <Bell className="mr-2 h-4 w-4 text-teal-400" />
                    <span>Notification Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800"
                    onClick={() => navigate('/profile')}
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-slate-800 text-red-400"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-teal-600 hover:bg-teal-500 text-white text-sm glow-teal"
                data-testid="signin-btn"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
        
        <ScannerBar isPlaying={scannerPlaying} drillPath={drillPath} />
      </header>

      {/* Tank's OverWatch Banner */}
      <TankBanner token={token} />

      {/* Quick Access Tiles */}
      <QuickAccessTiles 
        onToggleCameras={() => setMapLayers(prev => ({ ...prev, cameras: true }))}
        onOpenScanners={() => {
          setScannerOpen(true);
          setScannerMinimized(false);
        }}
        onToggleWeather={() => setMapLayers(prev => ({ ...prev, weather: true }))}
        onOpenAiChat={() => setAiChatOpen(true)}
      />

      {/* Left Sidebar - SCANNER FEEDS */}
      <aside 
        className="hidden lg:block absolute top-[165px] left-4 bottom-4 w-96 z-20 glass-card overflow-hidden"
        data-testid="scanner-sidebar"
      >
        <ScannerFeedsSidebar onScannerPlay={handleScannerPlay} />
      </aside>

      {/* Mobile Scanner Feeds Toggle */}
      <div className="lg:hidden absolute bottom-4 left-4 right-4 z-10">
        <Sheet>
          <SheetTrigger asChild>
            <Button 
              className="w-full glass-card bg-slate-900/90 hover:bg-slate-800 border border-slate-700 text-slate-200"
              data-testid="mobile-feed-trigger"
            >
              <Radio className="w-4 h-4 mr-2 text-teal-500" />
              Scanner Feeds
              <Badge className="ml-2 bg-teal-600 text-white">LIVE</Badge>
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[70vh] glass-card border-slate-800 p-0 rounded-t-xl">
            <ScannerFeedsSidebar onScannerPlay={handleScannerPlay} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Scanner Feed Panel */}
      <ScannerFeed 
        isOpen={scannerOpen}
        onClose={() => {
          setScannerOpen(false);
          setSelectedScannerFeed(null);
        }}
        onMinimize={() => setScannerMinimized(!scannerMinimized)}
        isMinimized={scannerMinimized}
        preselectedFeed={selectedScannerFeed}
      />

      {/* AI Chat */}
      <AIChat 
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        token={token}
        userLocation={drillPath.state ? { state: drillPath.state, city: drillPath.city } : null}
      />

      {/* AI Floating Button */}
      {!aiChatOpen && (
        <AIFloatingButton onClick={() => setAiChatOpen(true)} />
      )}

      {/* Notification Settings Sheet */}
      <Sheet open={notifSettingsOpen} onOpenChange={setNotifSettingsOpen}>
        <SheetContent side="right" className="w-[400px] glass-card border-slate-800 overflow-y-auto">
          <div className="py-4">
            <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-teal-400" />
              Notification Settings
            </h2>
            <NotificationSettings />
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Trial Warning Modal */}
      <TrialWarningModal />
      
      {/* Layer Details Dialog */}
      <LayerDetailsDialog 
        isOpen={layerDetailsOpen}
        onClose={() => setLayerDetailsOpen(false)}
        selectedLayer={selectedLayerDetails}
        token={token}
        userLocation={userLocation}
      />
      
      {/* Scanner Transcription Panel */}
      <ScannerTranscriptionPanel 
        isOpen={transcriptionOpen}
        onClose={() => setTranscriptionOpen(false)}
      />
      
      {/* Transcription History Panel */}
      <TranscriptionHistoryPanel 
        isOpen={transcriptionHistoryOpen}
        onClose={() => setTranscriptionHistoryOpen(false)}
      />
      
      {/* Transcription Floating Button - shows when scanner is playing */}
      <TranscriptionFloatingButton onClick={() => setTranscriptionOpen(true)} />
      
      {/* Transcription History Button - always visible */}
      <TranscriptionHistoryButton onClick={() => setTranscriptionHistoryOpen(true)} />
      
      {/* Gamification Panel */}
      <GamificationPanel 
        isOpen={gamificationOpen}
        onClose={() => setGamificationOpen(false)}
      />
      
      {/* Watch Zone Alerts Panel */}
      <WatchZoneAlertsPanel 
        isOpen={watchZoneAlertsOpen}
        onClose={() => setWatchZoneAlertsOpen(false)}
      />
      
      {/* I'm Safe Floating Button */}
      <ImSafeButton variant="floating" />
      
      {/* Bottom Ticker */}
      <BottomTicker />
    </div>
  );
};

// Wrapper component that provides the DashboardContext and AudioContext
const Dashboard = () => {
  return (
    <DashboardProvider>
      <AudioProvider>
        <DashboardContent />
      </AudioProvider>
    </DashboardProvider>
  );
};

export default Dashboard;
